import { useState } from 'react';
import { Badge, Button, Card, Group, Pagination, SegmentedControl, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { modals } from '@mantine/modals';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { api } from '@/api/client';
import type { AdminUser, Paged, PushLog } from '@/api/types';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { formatMsk } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';

const PLATFORM_LABEL: Record<string, string> = { all: 'Все', ios: 'iOS', android: 'Android' };
const STATUS_META: Record<string, { label: string; color: string }> = {
    sent: { label: 'отправлено', color: 'brand' },
    partial: { label: 'частично', color: 'yellow' },
    failed: { label: 'ошибка', color: 'red' },
    queued: { label: 'в очереди', color: 'gray' },
};

interface PushResult {
    status: string;
    devices_sent: number;
    devices_total: number;
}

function Composer() {
    const queryClient = useQueryClient();
    const [target, setTarget] = useState<'user' | 'all'>('user');
    const [platform, setPlatform] = useState('all');
    const [userId, setUserId] = useState<string | null>(null);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');

    const users = useQuery({
        queryKey: ['users'],
        queryFn: () => api.get<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users ?? []),
    });

    const send = useMutation({
        mutationFn: () =>
            api.post<PushResult>('/api/admin/notifications/push', {
                target,
                platform,
                title: title.trim(),
                body: body.trim(),
                ...(target === 'user' ? { user_id: Number(userId) } : {}),
            }),
        onSuccess: (res) => {
            const msg = `Доставлено ${res.devices_sent}/${res.devices_total} устройств`;
            if (res.status === 'failed') notifyError(new Error(msg));
            else notifySuccess(msg);
            setTitle('');
            setBody('');
            queryClient.invalidateQueries({ queryKey: ['push-logs'] });
        },
        onError: notifyError,
    });

    const ready = title.trim() && body.trim() && (target === 'all' || userId);

    const submit = () => {
        if (target === 'user') return send.mutate();
        modals.openConfirmModal({
            title: 'Рассылка всем',
            children: (
                <Text size="sm">
                    Отправить push всем пользователям приложения{platform !== 'all' ? ` (${PLATFORM_LABEL[platform]})` : ''}?
                </Text>
            ),
            labels: { confirm: 'Отправить', cancel: 'Отмена' },
            onConfirm: () => send.mutate(),
        });
    };

    return (
        <Card maw={640}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
            >
                <Stack>
                    <Text fw={700}>Новая рассылка</Text>
                    <Text size="xs" c="dimmed" mt={-8}>
                        Push в клиентское приложение iOS/Android.
                    </Text>
                    <SegmentedControl
                        data={[
                            { value: 'user', label: 'Пользователю' },
                            { value: 'all', label: 'Всем' },
                        ]}
                        value={target}
                        onChange={(v) => setTarget(v as 'user' | 'all')}
                    />
                    <SegmentedControl
                        data={[
                            { value: 'all', label: 'Все платформы' },
                            { value: 'ios', label: 'iOS' },
                            { value: 'android', label: 'Android' },
                        ]}
                        value={platform}
                        onChange={setPlatform}
                    />
                    {target === 'user' && (
                        <Select
                            label="Пользователь"
                            placeholder="Начните вводить имя или @username"
                            searchable
                            nothingFoundMessage="Не нашли"
                            data={(users.data ?? []).map((u) => ({
                                value: String(u.id),
                                label: `${u.name || 'Без имени'}${u.username ? ` (@${u.username})` : ''} · #${u.id}`,
                            }))}
                            value={userId}
                            onChange={setUserId}
                        />
                    )}
                    <TextInput
                        label="Заголовок"
                        maxLength={256}
                        placeholder="Например: скидка 20% на стирку"
                        value={title}
                        onChange={(e) => setTitle(e.currentTarget.value)}
                    />
                    <Textarea
                        label="Текст"
                        maxLength={2000}
                        autosize
                        minRows={3}
                        value={body}
                        onChange={(e) => setBody(e.currentTarget.value)}
                    />
                    <Group justify="flex-end">
                        <Button type="submit" leftSection={<Send size={16} />} disabled={!ready} loading={send.isPending}>
                            Отправить
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Card>
    );
}

const logColumns: Column<PushLog>[] = [
    { key: 'title', title: 'Заголовок', mobile: 'title', render: (l) => l.title || '—' },
    { key: 'date', title: 'Дата', mobile: 'subtitle', render: (l) => formatMsk(l.created_at) },
    {
        key: 'whom',
        title: 'Кому',
        render: (l) =>
            l.target === 'all'
                ? 'Все устройства'
                : `${l.user_name || 'Пользователь'}${l.user_login ? ` (@${l.user_login})` : ''} · #${l.user_id ?? '—'}`,
    },
    { key: 'platform', title: 'Платформа', render: (l) => PLATFORM_LABEL[l.platform || 'all'] ?? l.platform },
    {
        key: 'status',
        title: 'Статус',
        render: (l) => {
            const meta = STATUS_META[l.status] ?? { label: l.status, color: 'gray' };
            return <Badge color={meta.color}>{meta.label}</Badge>;
        },
    },
    {
        key: 'devices',
        title: 'Устройств',
        render: (l) => `${l.devices_sent}/${l.devices_total}${l.devices_failed ? ` (−${l.devices_failed})` : ''}`,
    },
    { key: 'admin', title: 'Отправил', render: (l) => l.admin_name || '—' },
];

export function BroadcastsScreen() {
    const [page, setPage] = useState(1);
    const logs = useQuery({
        queryKey: ['push-logs', page],
        queryFn: () => api.get<Paged & { logs: PushLog[] }>(`/api/admin/notifications/logs?page=${page}`),
        placeholderData: keepPreviousData,
    });

    return (
        <Page title="Рассылки">
            <Composer />
            <Text fw={800} fz="lg">
                История
            </Text>
            <QueryState isLoading={logs.isLoading} error={logs.error} onRetry={logs.refetch}>
                <ResponsiveTable data={logs.data?.logs ?? []} columns={logColumns} rowKey={(l) => l.id} empty="Рассылок пока не было" />
                {(logs.data?.pages ?? 1) > 1 && (
                    <Group justify="center">
                        <Pagination total={logs.data!.pages} value={page} onChange={setPage} />
                    </Group>
                )}
            </QueryState>
        </Page>
    );
}
